import React from 'react';
import PropTypes from 'prop-types';
import ClipLoader from 'react-spinners/ClipLoader';
import Button from '../../../Button';
import styles from './StudentImport.module.sass';

const findingShape = PropTypes.shape({
  severity: PropTypes.string,
  message: PropTypes.string,
});

const totalsShape = PropTypes.shape({
  prior: PropTypes.number,
  incoming: PropTypes.number,
  file: PropTypes.number,
  delta: PropTypes.number,
  pct_change: PropTypes.number,
});

const gradeShape = PropTypes.shape({
  grade: PropTypes.string,
  label: PropTypes.string,
  prior: PropTypes.number,
  incoming: PropTypes.number,
  delta: PropTypes.number,
  pct_change: PropTypes.number,
  severity: PropTypes.string,
});

const sampleShape = PropTypes.shape({
  student_id: PropTypes.string,
  name: PropTypes.string,
  grade: PropTypes.string,
});

const idsShape = PropTypes.shape({
  matched: PropTypes.number,
  new: PropTypes.number,
  new_in_entry_grades: PropTypes.number,
  missing: PropTypes.number,
  returning_match_pct: PropTypes.number,
  entry_grades: PropTypes.arrayOf(PropTypes.string),
  sample_new: PropTypes.arrayOf(sampleShape),
  sample_missing: PropTypes.arrayOf(sampleShape),
});

const promotionShape = PropTypes.shape({
  comparable: PropTypes.number,
  advanced_pct: PropTypes.number,
  repeated_pct: PropTypes.number,
});

const SEVERITY_CLASS = {
  alert: styles.severityAlert,
  warn: styles.severityWarn,
  new: styles.severityNew,
  info: styles.severityInfo,
  ok: styles.severityOk,
};

const formatSeason = (season) => (season ? season.replace(/^([a-z]+)/i, (m) => m.charAt(0).toUpperCase() + m.slice(1)) : '');

const formatDelta = (delta, pct) => {
  if (delta === 0) return 'no change';
  const sign = delta > 0 ? '+' : '';
  return pct === null || pct === undefined ? `${sign}${delta}` : `${sign}${delta} (${sign}${pct}%)`;
};

const Findings = ({ findings }) => {
  if (!findings || findings.length === 0) {
    return (
      <p className={`${styles.finding} ${styles.severityOk}`}>
        Nothing looks out of place next to last season.
      </p>
    );
  }

  return (
    <ul className={styles.findingList}>
      {findings.map((finding) => (
        <li key={finding.message} className={`${styles.finding} ${SEVERITY_CLASS[finding.severity] || ''}`}>
          {finding.message}
        </li>
      ))}
    </ul>
  );
};

Findings.propTypes = { findings: PropTypes.arrayOf(findingShape) };
Findings.defaultProps = { findings: [] };

const TotalCard = ({ label, priorSeason, season, totals }) => (
  <div className={styles.statCard}>
    <div className={styles.statLabel}>{label}</div>
    <div className={styles.statNumbers}>
      <span className={styles.statPrior}>
        {totals.prior}
        <small>{formatSeason(priorSeason)}</small>
      </span>
      <span className={styles.statArrow}>&rarr;</span>
      <span className={styles.statIncoming}>
        {totals.incoming}
        <small>{formatSeason(season)}</small>
      </span>
    </div>
    <div className={styles.statDelta}>{formatDelta(totals.delta, totals.pct_change)}</div>
  </div>
);

TotalCard.propTypes = {
  label: PropTypes.string.isRequired,
  priorSeason: PropTypes.string,
  season: PropTypes.string,
  totals: totalsShape.isRequired,
};
TotalCard.defaultProps = { priorSeason: '', season: '' };

const GradeTable = ({ grades, priorSeason, season }) => {
  if (!grades || grades.length === 0) return null;

  return (
    <table className={styles.compareTable}>
      <thead>
        <tr>
          <th>Grade</th>
          <th>{formatSeason(priorSeason)}</th>
          <th>{formatSeason(season)}</th>
          <th>Change</th>
        </tr>
      </thead>
      <tbody>
        {grades.map((row) => (
          <tr key={row.grade} className={SEVERITY_CLASS[row.severity] || ''}>
            <td>{row.label}</td>
            <td>{row.prior}</td>
            <td>{row.incoming}</td>
            <td>{formatDelta(row.delta, row.pct_change)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

GradeTable.propTypes = {
  grades: PropTypes.arrayOf(gradeShape),
  priorSeason: PropTypes.string,
  season: PropTypes.string,
};
GradeTable.defaultProps = { grades: [], priorSeason: '', season: '' };

const SampleList = ({ title, rows }) => {
  if (!rows || rows.length === 0) return null;

  return (
    <div className={styles.sampleList}>
      <strong>{title}</strong>
      <ul>
        {rows.map((row) => (
          <li key={row.student_id}>
            {row.student_id}
            {row.name ? ` — ${row.name}` : ''}
            {row.grade ? ` (${row.grade})` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
};

SampleList.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(sampleShape),
};
SampleList.defaultProps = { rows: [] };

const IdSummary = ({ ids, priorSeason }) => {
  if (!ids) return null;

  return (
    <div className={styles.idSummary}>
      <div className={styles.idRow}>
        <span><strong>{ids.matched}</strong> returning (ID also in {formatSeason(priorSeason)})</span>
        <span><strong>{ids.new}</strong> new IDs — {ids.new_in_entry_grades} in {ids.entry_grades.join(' / ')}</span>
        <span><strong>{ids.missing}</strong> from last season are not in this file</span>
      </div>

      {ids.returning_match_pct !== null && ids.returning_match_pct !== undefined && (
        <p className={styles.idMatchRate}>
          {ids.returning_match_pct}% of the students who should be returning match an ID from {formatSeason(priorSeason)}.
        </p>
      )}

      <div className={styles.sampleColumns}>
        <SampleList title="Sample of new IDs" rows={ids.sample_new} />
        <SampleList title="Sample not in this file" rows={ids.sample_missing} />
      </div>
    </div>
  );
};

IdSummary.propTypes = {
  ids: idsShape,
  priorSeason: PropTypes.string,
};
IdSummary.defaultProps = { ids: null, priorSeason: '' };

const Promotion = ({ promotion, priorSeason }) => {
  if (!promotion) return null;

  return (
    <p className={styles.promotion}>
      Of {promotion.comparable} returning students with a numeric grade,{' '}
      <strong>{promotion.advanced_pct}%</strong> moved up exactly one grade and{' '}
      <strong>{promotion.repeated_pct}%</strong> are in the same grade as {formatSeason(priorSeason)}.
    </p>
  );
};

Promotion.propTypes = {
  promotion: promotionShape,
  priorSeason: PropTypes.string,
};
Promotion.defaultProps = { promotion: null, priorSeason: '' };

const ImportPreview = ({ comparison, loading, error, onCancel, onContinue }) => {
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <p>Comparing this file to last season&apos;s roster…</p>
        <ClipLoader color="blue" loading size={120} />
      </div>
    );
  }

  const unavailable = error || !comparison || comparison.available === false;

  return (
    <div className={styles.preview}>
      <h3 className={styles.previewTitle}>
        Check this file against {formatSeason(comparison && comparison.prior_season)}
      </h3>

      {unavailable ? (
        <p className={styles.previewNote}>
          {error || (comparison && comparison.baseline_note)
            || 'There is no previous season to compare this file against.'}
        </p>
      ) : (
        <>
          <p className={styles.previewNote}>{comparison.baseline_note}</p>

          <Findings findings={comparison.findings} />

          <div className={styles.statRow}>
            {comparison.totals.students && (
              <TotalCard
                label="Students"
                totals={comparison.totals.students}
                priorSeason={comparison.prior_season}
                season={comparison.season}
              />
            )}
            {comparison.totals.staff && (
              <TotalCard
                label="Staff"
                totals={comparison.totals.staff}
                priorSeason={comparison.prior_season}
                season={comparison.season}
              />
            )}
          </div>

          <GradeTable
            grades={comparison.grades}
            priorSeason={comparison.prior_season}
            season={comparison.season}
          />

          <IdSummary ids={comparison.ids} priorSeason={comparison.prior_season} />

          <Promotion promotion={comparison.promotion} priorSeason={comparison.prior_season} />
        </>
      )}

      <div className={styles.buttonContainer}>
        <button type="button" className={`btn btn-default ${styles.btn}`} onClick={onCancel}>
          Cancel — do not import
        </button>

        <Button type="button" variant="primary" onClick={onContinue}>
          This looks right — continue
        </Button>
      </div>
    </div>
  );
};

ImportPreview.propTypes = {
  comparison: PropTypes.shape({
    available: PropTypes.bool,
    season: PropTypes.string,
    prior_season: PropTypes.string,
    baseline_note: PropTypes.string,
    findings: PropTypes.arrayOf(findingShape),
    totals: PropTypes.shape({ students: totalsShape, staff: totalsShape }),
    grades: PropTypes.arrayOf(gradeShape),
    ids: idsShape,
    promotion: promotionShape,
  }),
  loading: PropTypes.bool,
  error: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
};

ImportPreview.defaultProps = {
  comparison: null,
  loading: false,
  error: null,
};

export default ImportPreview;
